import type { ReactNode } from 'react';

/* Which side of the accounting funnel someone is on: starting a company
   (incorporate) or already running one (operate).

   The signed-in app has always known this — /setup-service writes
   `businessSetupData.type` — but a LEAD arrives long before any of that, so
   nothing in the `demoRequests` store said which of the two a new enquiry was.
   Every public lead form now asks, using the same two tracks the funnel itself
   uses, and admin Leads shows the answer.

   One component rather than a copy in each form: the demo funnel alone has four
   near-identical variants (/demo2, /dm0, /dm2, /dm4). */

export type SetupTrack = 'incorporate' | 'operate';

export const SETUP_TRACKS: { id: SetupTrack; title: string; hint: string }[] = [
  { id: 'incorporate', title: 'Setting up a new company', hint: 'Not registered yet' },
  { id: 'operate', title: 'I already have a company', hint: 'Registered and running' },
];

/** Short label for tables and pills. Empty string when nothing was captured. */
export const trackLabel = (v?: string | null) =>
  v === 'incorporate' ? 'Incorporate' : v === 'operate' ? 'Operate' : '';

/** Lets a CTA pre-answer the question, e.g. /demo1?track=incorporate. */
export function trackFromSearch(search: string): SetupTrack | '' {
  const params = new URLSearchParams(search);
  const raw = (params.get('track') || params.get('setup') || '').trim().toLowerCase();
  if (raw === 'incorporate' || raw === 'new' || raw === 'setup') return 'incorporate';
  if (raw === 'operate' || raw === 'existing') return 'operate';
  return '';
}

type Props = {
  value: SetupTrack | '';
  onChange: (v: SetupTrack) => void;
  label?: ReactNode;
  /** Marks the field optional in the label — the picker itself never blocks. */
  optional?: boolean;
};

export default function SetupTrackPicker({
  value,
  onChange,
  label = 'Where are you with your company?',
  optional,
}: Props) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-900 mb-2">
        {label}
        {optional && <span className="font-normal text-gray-400"> (optional)</span>}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SETUP_TRACKS.map(t => {
          const on = value === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              aria-pressed={on}
              className={`rounded-lg border-2 px-4 py-3 text-left transition ${
                on ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <span className={`block text-sm font-semibold ${on ? 'text-blue-800' : 'text-gray-900'}`}>
                {t.title}
              </span>
              <span className="mt-0.5 block text-xs text-gray-500">{t.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
